import HelpIcon from '@mui/icons-material/Help';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Drawing from '/@/components/drawing';
import useDebounce from '/@/hooks/debounce/useDebounce';
import {
    type HelpCategory as QuestionCategory,
    type HelpPageListItem as QuestionPage,
    useHelpCenterCategories,
    useHelpCenterPages,
} from '/@/hooks/help-center';
import { _ } from '/@/locale';
import { sanitize } from '/@/utils';
import { reverse } from '/@/utils/router';
import {
    HelpCategoryGroups,
    type HelpCenterCategoryGroup,
    HelpCenterHomeCategoriesGrid,
    HelpCenterHomeHero,
    HelpCenterHomeSearchField,
    HelpCenterHomeSearchResults,
} from './components';
import HelpCenterContainer from './components/container';

const buildGroups = (categories: QuestionCategory[], pages: QuestionPage[]): HelpCenterCategoryGroup[] => {
    const byCat = new Map<number, QuestionPage[]>();
    for (const p of pages) {
        const id = p.category.id;
        let list = byCat.get(id);
        if (!list) {
            list = [];
            byCat.set(id, list);
        }
        list.push(p);
    }
    const ordered = [...categories].filter((c) => byCat.has(c.id)).sort((a, b) => a.order - b.order || a.id - b.id);
    return ordered.map((category) => {
        const catPages = [...(byCat.get(category.id) ?? [])].sort((a, b) => {
            const po = a.order - b.order;
            if (po !== 0) {
                return po;
            }
            return a.id - b.id;
        });
        return { category, pages: catPages };
    });
};

const HelpCenterHome = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);
    const [expandedCats, setExpandedCats] = useState<Record<number, boolean>>({});
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(() => new Set());
    const [browseExpandedCats, setBrowseExpandedCats] = useState<Record<number, boolean>>({});

    const categoriesSwr = useHelpCenterCategories();
    const pagesSwr = useHelpCenterPages();

    const categories = categoriesSwr.data ?? [];
    const pages = pagesSwr.data ?? [];
    const loadError = Boolean(categoriesSwr.error || pagesSwr.error);
    const loading = categoriesSwr.isLoading || pagesSwr.isLoading;

    const q = sanitize(debouncedSearch).toLowerCase();
    const isSearching = Boolean(debouncedSearch);

    const filteredPages = pages.filter((p) => {
        if (!q) return true;
        const t = sanitize(_(p.title)).toLowerCase();
        const ct = sanitize(_(p.category.title)).toLowerCase();
        const cs = sanitize(_(p.category.subtitle)).toLowerCase();
        return t.includes(q) || ct.includes(q) || cs.includes(q);
    });

    const groups = useMemo(() => buildGroups(categories, pages), [categories, pages]);

    const displayedGroups = useMemo(() => {
        if (selectedCategoryIds.size === 0) {
            return groups;
        }
        return groups.filter((g) => selectedCategoryIds.has(g.category.id));
    }, [groups, selectedCategoryIds]);

    const searchResultsByCategory = useMemo(() => {
        if (!isSearching) return [];
        const byCat = new Map<number, { category: QuestionCategory; pages: QuestionPage[] }>();
        for (const p of filteredPages) {
            const id = p.category.id;
            let group = byCat.get(id);
            if (!group) {
                group = { category: p.category, pages: [] };
                byCat.set(id, group);
            }
            group.pages.push(p);
        }
        const resultGroups = [...byCat.values()];
        for (const g of resultGroups) {
            g.pages.sort((a, b) => {
                const po = a.order - b.order;
                if (po !== 0) return po;
                return a.id - b.id;
            });
        }
        resultGroups.sort((a, b) => a.category.order - b.category.order || a.category.id - b.category.id);
        return resultGroups;
    }, [filteredPages, isSearching]);

    const isCatExpanded = (categoryId: number) => expandedCats[categoryId] !== false;

    const toggleCategoryExpanded = (categoryId: number) => {
        setExpandedCats((prev) => {
            const expanded = prev[categoryId] !== false;
            return { ...prev, [categoryId]: !expanded };
        });
    };

    const toggleChipCategory = (categoryId: number) => {
        setSelectedCategoryIds((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const isBrowseCategoryExpanded = (categoryId: number) => Boolean(browseExpandedCats[categoryId]);

    const toggleBrowseCategory = (categoryId: number) => {
        setBrowseExpandedCats((prev) => {
            const expanded = Boolean(prev[categoryId]);
            return { ...prev, [categoryId]: !expanded };
        });
    };

    const goToArticle = (slug: string) => {
        navigate(
            reverse('helpCenter.Article', {
                params: { slug },
            })
        );
    };

    const chipCategories = useMemo(() => {
        if (isSearching) {
            return groups
                .map((g) => g.category)
                .filter((c) => searchResultsByCategory.some((g) => g.category.id === c.id));
        }
        return groups.filter((g) => g.pages.length > 0).map((g) => g.category);
    }, [groups, searchResultsByCategory, isSearching]);

    return (
        <HelpCenterContainer maxWidth='md'>
            <Stack spacing={5} sx={{ pb: 6, alignItems: 'center', width: '100%' }}>
                <Stack spacing={4} sx={{ alignItems: 'center', width: '100%' }}>
                    {!isSearching && <HelpCenterHomeHero />}
                    <Stack spacing={2} width='100%'>
                        <HelpCenterHomeSearchField
                            value={search}
                            onChange={setSearch}
                            isSearching={isSearching}
                            onClear={() => setSearch('')}
                        />
                        {!loading && !loadError && chipCategories.length > 0 && (
                            <HelpCenterHomeCategoriesGrid
                                categories={chipCategories}
                                selectedCategoryIds={selectedCategoryIds}
                                onToggleCategorySelection={toggleChipCategory}
                            />
                        )}
                    </Stack>
                </Stack>

                {loadError && <Typography color='error'>{_('No se pudo cargar el centro de ayuda.')}</Typography>}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && !loadError && !isSearching && groups.length === 0 && pages.length === 0 && (
                    <Drawing type='library-not-found' title={_('No hay articulos disponibles.')} />
                )}

                {!loading && !loadError && isSearching && searchResultsByCategory.length === 0 && (
                    <Drawing type='error2' title={_('No hay resultados para tu búsqueda.')} />
                )}

                {!loading && !loadError && isSearching && searchResultsByCategory.length > 0 && (
                    <HelpCenterHomeSearchResults
                        groups={searchResultsByCategory}
                        isCategoryExpanded={isCatExpanded}
                        onToggleCategory={toggleCategoryExpanded}
                        onSelectPage={goToArticle}
                    />
                )}

                {!loading && !loadError && !isSearching && displayedGroups.length > 0 && (
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <HelpIcon sx={{ fontSize: 24 }} color='primary' />
                            <Typography variant='h6' color='primary'>
                                {_('Preguntas frecuentes')}
                            </Typography>
                        </Stack>
                        <HelpCategoryGroups
                            groups={displayedGroups}
                            isCategoryExpanded={isBrowseCategoryExpanded}
                            onToggleCategory={toggleBrowseCategory}
                            onSelectPage={goToArticle}
                        />
                    </Stack>
                )}
            </Stack>
        </HelpCenterContainer>
    );
};

export default HelpCenterHome;
